import React, { useEffect, useMemo, useState } from "react";
import Input from "../../../components/reusable-input/Input";
import Table from "../../../components/reusable-table/Table";
import "./UserGroups.css";
import {
  DeleteIcon,
  EditIcon,
  MessageSquareIcon,
  SearchIcon,
} from "../../../icons/reactIcons";
import CreateEditModal from "./group-modals/CreateEditModal";
import GroupSendMessageModal from "./group-modals/GroupSendMessageModal";
import ConfirmUserActionModal from "../../../components/confirm-modal/ConfirmUserActionModal";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import {
  fetchGroups,
  createGroupThunk,
  updateGroupThunk,
} from "../../../redux/slices/userGroupSlice";
import {
  clearSearchResults,
  fetchUserList,
  searchUsers,
} from "../../../redux/slices/userListSlice";
import Loader from "../../../components/custom-loader/Loader";
import { sendCmsMessage } from "../../../redux/slices/cmsMessageSlice";
import { formatDateTime } from "../../../utils/formatDateTime";

const columns = [
  { header: "Group Name", accessor: "groupName" },
  { header: "Members", accessor: "members" },
  { header: "Created By", accessor: "created_by" },
  { header: "Updated By", accessor: "updated_by" },
];

const UserGroups: React.FC = () => {
  const dispatch = useAppDispatch();

  const { groups, loading } = useAppSelector((state) => state.groups);
  const { users } = useAppSelector((state) => state.userList);
  const { createLoading } = useAppSelector((state) => state.cmsMessages);
  const { searchResults, searchLoading } = useAppSelector(
    (state) => state.userList,
  );
  const debounceRef = React.useRef<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editData, setEditData] = useState<any>(null);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageGroup, setMessageGroup] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [deleteGroupData, setDeleteGroupData] = useState<any>(null);

  useEffect(() => {
    if (!groups.length) {
      dispatch(fetchGroups());
    }
    if (!users.length) {
      dispatch(
        fetchUserList({
          pageId: 1,
          limit: 30,
          status: "active",
        }),
      );
    }
  }, [dispatch]);

  const handleUserSearch = (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (value.length >= 3) {
        dispatch(searchUsers(value));
      } else {
        dispatch(clearSearchResults());
      }
    }, 400);
  };

  const usersInGroupsMap = useMemo(() => {
    const map = new Map<number, number>();

    groups.forEach((group: any) => {
      group.user?.forEach((u: any) => {
        map.set(u.id, group.id);
      });
    });

    return map;
  }, [groups]);

  const filteredMembers = useMemo(() => {
    return searchResults
      .map((user: any) => ({
        id: user.id,
        name:
          `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
          user.email ||
          "Unknown",
        email: user.email || "",
      }))
      .filter((member) => {
        const assignedGroupId = usersInGroupsMap.get(member.id);

        if (modalMode === "create") {
          return !assignedGroupId;
        }

        if (modalMode === "edit") {
          return !assignedGroupId || assignedGroupId === editData?.groupId;
        }

        return true;
      });
  }, [searchResults, usersInGroupsMap, modalMode, editData]);

  const tableData = useMemo(() => {
    return groups.map((group: any) => ({
      id: group.id,
      groupName: group.name || "",
      members: group.user?.length || 0,
      created_by: (
        <div className="merged-cell">
          <div className="merged-label">{group.creator?.name || "-"}</div>
          <div className="merged-date">
            {group.createdAt ? formatDateTime(group.createdAt) : "-"}
          </div>
        </div>
      ),
      updated_by: (
        <div className="merged-cell">
          {!group.updatedBy ? (
            "-"
          ) : (
            <>
              <div className="merged-label">{group.updater?.name}</div>
              <div className="merged-date">
                {group.updatedAt ? formatDateTime(group.updatedAt) : "-"}
              </div>
            </>
          )}
        </div>
      ),
      raw: group,
    }));
  }, [groups]);

  const filteredData = useMemo(() => {
    return tableData.filter((row: any) =>
      (row.groupName ?? "").toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [tableData, searchTerm]);

  const openMessageModal = (row: any) => {
    setMessageGroup({
      groupName: row.groupName,
      members: row.raw.user || [],
    });
    setIsMessageModalOpen(true);
  };

  const openCreateModal = () => {
    setModalMode("create");
    setEditData(null);
    setIsModalOpen(true);
  };

  const openEditModal = (row: any) => {
    setModalMode("edit");
    console.log("row", row);
    const safeMembers =
      row.raw.user?.map((u: any) => ({
        id: u.id,
        name:
          `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
          u.email ||
          "Unknown",
        email: u.email || "",
      })) || [];

    setEditData({
      groupId: row.id,
      groupName: row.groupName,
      members: safeMembers,
      originalMembers: safeMembers,
    });

    setIsModalOpen(true);
  };

  const handleDeleteModalOpen = (row: any) => {
    setGroupName(row.groupName);

    setDeleteGroupData({
      groupId: row.id,
      users: row.raw.user || [],
    });

    setOpenDeleteModal(true);
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupData) return;

    const payload = {
      name: "", // backend expects empty string
      isActive: false,
      users: deleteGroupData.users.map((u: any) => ({
        userId: u.id,
        isActive: true, // keep users active
      })),
    };

    try {
      await dispatch(
        updateGroupThunk({
          groupId: deleteGroupData.groupId,
          payload,
        }),
      ).unwrap();

      setOpenDeleteModal(false);
      setDeleteGroupData(null);
    } catch (error) {
      console.error("Delete group failed:", error);
      // modal stays open → good UX
    }
  };

  const actions = [
    {
      label: <MessageSquareIcon style={{ fontSize: "20px" }} />,
      onClick: openMessageModal,
    },
    {
      label: <DeleteIcon style={{ fontSize: "20px" }} />,
      onClick: handleDeleteModalOpen,
    },
    {
      label: <EditIcon style={{ fontSize: "20px" }} />,
      onClick: openEditModal,
    },
  ];

  const handleGroupSubmit = async (
    groupName: string,
    members: any[],
    originalMembers: any[] = [],
  ) => {
    let usersPayload: any[] = [];

    if (modalMode === "create") {
      usersPayload = members.map((u) => ({
        userId: u.id,
        isActive: true,
      }));
    }

    if (modalMode === "edit") {
      const currentIds = new Set(members.map((m) => m.id));

      usersPayload = [
        // still active members
        ...members.map((u) => ({
          userId: u.id,
          isActive: true,
        })),

        // removed members → isActive false
        ...originalMembers
          .filter((u) => !currentIds.has(u.id))
          .map((u) => ({
            userId: u.id,
            isActive: false,
          })),
      ];
    }

    const payload = {
      name: groupName,
      isActive: true,
      users: usersPayload,
    };

    try {
      if (modalMode === "create") {
        await dispatch(createGroupThunk(payload)).unwrap();
      }

      if (modalMode === "edit") {
        await dispatch(
          updateGroupThunk({
            groupId: editData.groupId,
            payload,
          }),
        ).unwrap();
      }

      // ✅ ONLY close modal after success
      setIsModalOpen(false);
    } catch (error) {
      console.error("Group submit failed:", error);
      // ❌ Modal stays open
      // optionally show toast/snackbar here
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!messageGroup) return;

    const payload = {
      message,
      users: messageGroup.members.map((u: any) => ({
        id: String(u.id),
        isActive: "true",
      })),
    };

    try {
      await dispatch(sendCmsMessage(payload)).unwrap();

      // Close modal only if success
      setIsMessageModalOpen(false);
      setMessageGroup(null);
    } catch (error) {
      console.error("Send message failed:", error);
      // Modal stays open for retry
    }
  };

  if (loading) {
    return (
      <div className="loading-text">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="user-groups-container">
        <div className="user-groups-header">
          <h2>User Groups</h2>

          <div style={{ display: "flex", gap: "1.5rem" }}>
            <Input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              search
              startIcon={<SearchIcon fontSize={"20px"} />}
              removeBottomMargin
            />

            <button className="user-groups-btn" onClick={openCreateModal}>
              Create New
            </button>
          </div>
        </div>

        <Table
          columns={columns}
          data={filteredData}
          rowsPerPage={20}
          actions={actions}
          rounded
        />
      </div>

      {isModalOpen && (
        <CreateEditModal
          mode={modalMode}
          initialName={editData?.groupName}
          initialMembers={editData?.members || []}
          allMembers={filteredMembers}
          originalMembers={editData?.originalMembers || []}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleGroupSubmit}
          onSearchChange={handleUserSearch} // ✅ NEW
          loading={searchLoading}
        />
      )}

      {isMessageModalOpen && messageGroup && (
        <GroupSendMessageModal
          groupName={messageGroup.groupName}
          selectedMembers={messageGroup.members}
          onClose={() => setIsMessageModalOpen(false)}
          onSend={handleSendMessage}
          loading={createLoading}
        />
      )}

      {openDeleteModal && (
        <ConfirmUserActionModal
          type="delete"
          userName={groupName}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={handleDeleteGroup}
        />
      )}
    </>
  );
};

export default UserGroups;
